import { ArrowLeft, FileSpreadsheet, FileSliders, Brain, BarChart3, Zap, Code, Layers, FileText, TrendingUp, Clock, Shield } from 'lucide-react'
import { Link } from 'react-router-dom'

const features = [
  {
    icon: <FileSpreadsheet size={24} />,
    title: 'Excel/JSON Input',
    description: 'Accepts structured data from Excel spreadsheets or JSON files. Automatic schema detection and data validation with detailed error reporting for malformed inputs.',
  },
  {
    icon: <FileSliders size={24} />,
    title: 'Native PowerPoint Generation',
    description: 'Creates fully native .pptx files with proper slide masters, formatted text, tables, and embedded charts using python-pptx. No template dependencies required.',
  },
  {
    icon: <BarChart3 size={24} />,
    title: 'Automatic Chart Creation',
    description: 'Generates native PowerPoint charts (bar, line, pie, scatter) directly from input data with proper axis labels, legends, data labels, and styling matching the presentation theme.',
  },
  {
    icon: <Brain size={24} />,
    title: 'AI-Generated Analysis',
    description: 'Uses OpenAI API to generate contextual analysis text, executive summaries, key insights, and recommendations based on the input data. Temperature-tuned for professional tone.',
  },
  {
    icon: <Layers size={24} />,
    title: 'Intelligent Slide Layout',
    description: 'Automatically determines optimal slide structure based on data complexity — title slides, data tables, chart slides, analysis slides, and summary slides are composed intelligently.',
  },
  {
    icon: <Shield size={24} />,
    title: 'Error Handling & Validation',
    description: 'Comprehensive input validation with graceful error handling. Detailed logs for debugging, with user-friendly error messages for common data formatting issues.',
  },
]

const techStack = [
  { category: 'Language', tech: 'Python 3.11+', purpose: 'Primary language with modern type hints, async support, and rich ecosystem' },
  { category: 'PPT Engine', tech: 'python-pptx', purpose: 'Native PowerPoint file creation with full control over slides, shapes, and charts' },
  { category: 'AI Service', tech: 'OpenAI API', purpose: 'GPT-4 for contextual text generation, data analysis, and insight extraction' },
  { category: 'Data Processing', tech: 'pandas + openpyxl', purpose: 'Excel file parsing, data transformation, and structured data validation' },
  { category: 'Chart Engine', tech: 'python-pptx charts', purpose: 'Native PPTX chart objects (not images) with editable data and formatting' },
  { category: 'CLI', tech: 'argparse + rich', purpose: 'Command-line interface with colorful output, progress bars, and formatted tables' },
]

const impactMetrics = [
  {
    metric: '80%',
    label: 'Effort Reduction',
    description: 'Reduced manual slide creation effort by 80%, transforming hours of formatting work into seconds of automated generation.',
    icon: <TrendingUp size={28} />,
  },
  {
    metric: '~90%',
    label: 'Time Saved',
    description: 'Presentation creation time dropped from 2-3 hours to under 5 minutes for standard data-driven presentations.',
    icon: <Clock size={28} />,
  },
  {
    metric: '100%',
    label: 'Consistency',
    description: 'Every generated presentation follows the same professional formatting standards, eliminating human inconsistency.',
    icon: <Shield size={28} />,
  },
]

export default function PptMakerPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-xl border-b border-white/5">
        <div className="flex justify-between items-center px-8 py-4 max-w-7xl mx-auto">
          <Link to="/" className="text-xl font-bold tracking-tighter text-white font-headline">
            Neural Slate
          </Link>
          <Link
            to="/#projects"
            className="text-on-surface-variant hover:text-primary transition-colors flex items-center gap-2"
          >
            <ArrowLeft size={16} />
            Back to Projects
          </Link>
        </div>
      </header>

      <main className="pt-24">
        {/* Hero Section */}
        <section className="relative min-h-[600px] flex items-center px-8 overflow-hidden">
          <div className="absolute bottom-0 left-0 w-1/2 h-full bg-gradient-to-r from-tertiary/10 to-transparent blur-3xl pointer-events-none"></div>
          <div className="max-w-7xl mx-auto w-full relative z-10">
            <div className="max-w-4xl">
              <span className="inline-block py-1 px-3 mb-6 bg-surface-container-high border border-outline-variant/20 rounded-full text-primary text-xs font-mono tracking-widest uppercase">
                Internal Automation Tool · Python
              </span>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tighter mb-8 leading-tight font-headline text-white">
                PPT Maker <br />
                <span className="text-primary">Presentation Automation</span>
              </h1>
              <p className="text-xl md:text-2xl text-on-surface-variant font-light mb-12 max-w-2xl leading-relaxed">
                Internal automation tool that transforms Excel/JSON data into professional PowerPoint slides with native charts and AI-generated analysis.
              </p>
              <div className="flex flex-wrap gap-8 items-center">
                <div className="flex flex-col">
                  <span className="text-primary text-4xl font-bold tracking-tighter font-headline">80%</span>
                  <span className="text-on-surface-variant text-sm uppercase tracking-widest font-medium">Effort Reduction</span>
                </div>
                <div className="h-12 w-px bg-outline-variant/30"></div>
                <div className="flex flex-col">
                  <span className="text-primary text-4xl font-bold tracking-tighter font-headline">5min</span>
                  <span className="text-on-surface-variant text-sm uppercase tracking-widest font-medium">Generation Time</span>
                </div>
                <div className="h-12 w-px bg-outline-variant/30"></div>
                <div className="flex flex-col">
                  <span className="text-secondary text-4xl font-bold tracking-tighter font-headline">AI</span>
                  <span className="text-on-surface-variant text-sm uppercase tracking-widest font-medium">Powered Analysis</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Overview */}
        <section className="py-24 px-8 bg-surface-container-lowest">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
              <div className="lg:col-span-1">
                <h2 className="text-4xl font-bold mb-6 text-white font-headline">Overview</h2>
                <p className="text-on-surface-variant mb-8 leading-relaxed">
                  PPT Maker was built to solve a recurring pain point: manually creating data-driven PowerPoint presentations is tedious, error-prone, and wastes hours on formatting instead of analysis.
                </p>
                <div className="p-6 bg-surface-container-low rounded-xl border border-outline-variant/10">
                  <Zap className="text-primary mb-3" size={28} />
                  <h4 className="font-bold mb-2 text-white">From Hours to Minutes</h4>
                  <p className="text-sm text-on-surface-variant leading-relaxed">
                    What previously took 2-3 hours of manual chart creation, data copying, and formatting is now accomplished in under 5 minutes with consistent, professional results.
                  </p>
                </div>
              </div>
              <div className="lg:col-span-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {features.map((feature, index) => (
                    <div key={index} className="bg-surface-container-low p-6 rounded-xl border border-outline-variant/10 hover:border-primary/30 transition-all">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                        {feature.icon}
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2 font-headline">{feature.title}</h3>
                      <p className="text-sm text-on-surface-variant leading-relaxed">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-24 px-8 bg-surface">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4 text-white font-headline">How It Works</h2>
              <p className="text-on-surface-variant max-w-2xl mx-auto">
                Four-stage pipeline from raw data input to polished presentation output.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10 relative">
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-background font-bold text-sm">1</div>
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <FileSpreadsheet size={24} />
                </div>
                <h3 className="text-lg font-bold text-white mb-3 font-headline">Data Ingestion</h3>
                <p className="text-sm text-on-surface-variant leading-relaxed">
                  Reads Excel or JSON input, validates schema, detects data types, and structures the data for presentation generation with comprehensive error reporting.
                </p>
              </div>
              <div className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10 relative">
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-background font-bold text-sm">2</div>
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <Brain size={24} />
                </div>
                <h3 className="text-lg font-bold text-white mb-3 font-headline">AI Analysis</h3>
                <p className="text-sm text-on-surface-variant leading-relaxed">
                  Sends structured data to OpenAI API for analysis generation. Receives executive summaries, key insights, trend observations, and actionable recommendations.
                </p>
              </div>
              <div className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10 relative">
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-background font-bold text-sm">3</div>
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <FileSliders size={24} />
                </div>
                <h3 className="text-lg font-bold text-white mb-3 font-headline">Slide Assembly</h3>
                <p className="text-sm text-on-surface-variant leading-relaxed">
                  Constructs slides with proper masters, inserts formatted tables, creates native charts with data binding, and positions all elements with precise layout control.
                </p>
              </div>
              <div className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10 relative">
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-background font-bold text-sm">4</div>
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <FileText size={24} />
                </div>
                <h3 className="text-lg font-bold text-white mb-3 font-headline">Output</h3>
                <p className="text-sm text-on-surface-variant leading-relaxed">
                  Exports a fully functional .pptx file with native editable charts, formatted text, consistent styling, and professional presentation quality.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Tech Stack */}
        <section className="py-24 px-8 bg-surface-container-lowest">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
              <div className="lg:col-span-1">
                <h2 className="text-4xl font-bold mb-6 text-white font-headline">Tech Stack</h2>
                <p className="text-on-surface-variant mb-8 leading-relaxed">
                  Python-based automation leveraging best-in-class libraries for Office document generation and AI-powered content creation.
                </p>
                <div className="p-6 bg-surface-container-low rounded-xl border border-outline-variant/10">
                  <Code className="text-primary mb-3" size={28} />
                  <h4 className="font-bold mb-2 text-white">Native PPTX Charts</h4>
                  <p className="text-sm text-on-surface-variant leading-relaxed">
                    Unlike screenshot-based approaches, all charts are native PowerPoint chart objects — fully editable with right-click data editing, proper legends, and animation support.
                  </p>
                </div>
              </div>
              <div className="lg:col-span-2">
                <div className="overflow-hidden rounded-xl border border-outline-variant/15">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-surface-container-high text-on-surface uppercase text-xs tracking-widest">
                      <tr>
                        <th className="px-6 py-4">Category</th>
                        <th className="px-6 py-4">Technology</th>
                        <th className="px-6 py-4">Purpose</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/10">
                      {techStack.map((row, index) => (
                        <tr key={index} className="hover:bg-surface-container-low transition-colors">
                          <td className="px-6 py-6 font-bold text-primary">{row.category}</td>
                          <td className="px-6 py-6 text-on-surface-variant font-mono text-sm">{row.tech}</td>
                          <td className="px-6 py-6 text-on-surface-variant text-sm">{row.purpose}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Impact Metrics */}
        <section className="py-24 px-8 bg-surface">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4 text-white font-headline">Impact Metrics</h2>
              <p className="text-on-surface-variant max-w-2xl mx-auto">
                Measurable improvement in presentation creation workflow efficiency and output quality.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {impactMetrics.map((item, index) => (
                <div key={index} className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10 text-center">
                  <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center text-primary mx-auto mb-6">
                    {item.icon}
                  </div>
                  <div className="text-5xl font-bold text-primary mb-2 font-headline">{item.metric}</div>
                  <h3 className="text-lg font-bold text-white mb-4 font-headline">{item.label}</h3>
                  <p className="text-sm text-on-surface-variant leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 px-8 bg-surface-container-lowest">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6 text-white font-headline">Interested in the Code?</h2>
            <p className="text-on-surface-variant mb-10 leading-relaxed">
              The full Python automation tool with OpenAI integration, native chart generation, and CLI interface is available for review.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="https://github.com/Viresh26"
                target="_blank"
                rel="noopener noreferrer"
                className="px-10 py-4 bg-primary text-on-tertiary font-headline font-bold rounded-xl transition-all hover:shadow-[0_0_30px_rgba(115,249,0,0.3)]"
              >
                GITHUB REPOSITORY
              </a>
              <Link
                to="/#projects"
                className="px-10 py-4 bg-surface-container-high text-on-surface font-headline font-bold rounded-xl border border-outline-variant/30 hover:bg-surface-container-highest transition-all"
              >
                VIEW ALL PROJECTS
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
    </div>
  )
}
